import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Mail, Linkedin, Github, Calendar } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useMagneticEffect } from "@/hooks/use3DTilt";

declare global {
  interface Window {
    Cal?: any;
  }
}

const Contact = () => {
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.1 });
  const emailBtn = useMagneticEffect(0.3);
  const linkedinBtn = useMagneticEffect(0.3);
  const githubBtn = useMagneticEffect(0.3);

  useEffect(() => {
    // Cal.com script is preloaded in LoadingScreen, just initialize the inline embed
    const initCalendar = () => {
      if (window.Cal?.ns?.["30min"]) {
        window.Cal.ns["30min"]("inline", {
          elementOrSelector: "#my-cal-inline-30min",
          config: { layout: "month_view" },
          calLink: "spilios-spiliopoulos-e7izyh/30min",
        });

        window.Cal.ns["30min"]("ui", {
          hideEventTypeDetails: false,
          layout: "month_view",
        });
      }
    };

    // Check if Cal is ready, otherwise wait for it
    if (window.Cal?.ns?.["30min"]) {
      initCalendar();
    } else {
      // Poll for Cal to be ready
      const interval = setInterval(() => {
        if (window.Cal?.ns?.["30min"]) {
          clearInterval(interval);
          initCalendar();
        }
      }, 100);

      // Cleanup
      return () => clearInterval(interval);
    }
  }, []);

  return (
    <section id="contact" className="py-32 px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Floating elements */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />

      <div
        ref={sectionRef}
        className={`container max-w-6xl mx-auto relative z-10 transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="text-center mb-16 space-y-6">
          <h2 className="text-4xl md:text-5xl font-bold text-violet-400 [text-shadow:0_0_30px_rgba(167,139,250,0.7),0_0_60px_rgba(139,92,246,0.4)]">
            Let's Connect
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Ready to transform your AI challenges into measurable business results?
            Let's discuss how I can help drive your next innovation.
          </p>
        </div>

        <div>
          <Card className="p-6 md:p-10 card-enhanced border-primary/10 relative overflow-hidden">
            <div className="relative z-10 space-y-8">
              <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center gap-2">
                  <Calendar className="w-6 h-6 text-primary" />
                  <h3 className="text-2xl font-semibold">Schedule a Call</h3>
                </div>
                <p className="text-muted-foreground">
                  Whether you're looking for ML expertise, automation solutions, or research collaboration
                </p>
              </div>

              {/* Cal.com Inline Calendar Embed - Full Width */}
              <div
                id="my-cal-inline-30min"
                className="w-full rounded-xl overflow-hidden bg-background/50 backdrop-blur-sm border border-border/30 shadow-inner"
                style={{ minHeight: "650px", height: "auto" }}
              />

              <div className="grid sm:grid-cols-3 gap-6 pt-6 border-t border-border/30">
                <Button
                  asChild
                  ref={emailBtn.ref}
                  onMouseMove={emailBtn.onMouseMove}
                  onMouseLeave={emailBtn.onMouseLeave}
                  size="lg"
                  className="magnetic-btn w-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 border-0 text-white"
                >
                  <a
                    href="mailto:sphliossp@gmail.com"
                    aria-label="Email Spilios Spiliopoulos"
                  >
                    <Mail className="mr-2 w-5 h-5 group-hover:animate-bounce" />
                    Email Me
                  </a>
                </Button>
                <Button
                  asChild
                  ref={linkedinBtn.ref}
                  onMouseMove={linkedinBtn.onMouseMove}
                  onMouseLeave={linkedinBtn.onMouseLeave}
                  size="lg"
                  className="magnetic-btn w-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 border-0 text-white"
                >
                  <a
                    href="https://www.linkedin.com/in/spilios-spiliopoulos-ai-ml/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Spilios Spiliopoulos on LinkedIn"
                  >
                    <Linkedin className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                    LinkedIn
                  </a>
                </Button>
                <Button
                  asChild
                  ref={githubBtn.ref}
                  onMouseMove={githubBtn.onMouseMove}
                  onMouseLeave={githubBtn.onMouseLeave}
                  size="lg"
                  className="magnetic-btn w-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group bg-gradient-to-r from-violet-600 to-violet-600 hover:from-violet-700 hover:to-violet-700 border-0 text-white"
                >
                  <a
                    href="https://github.com/spiliossp"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Spilios Spiliopoulos on GitHub"
                  >
                    <Github className="mr-2 w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                    GitHub
                  </a>
                </Button>
              </div>

              <div className="pt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Open to exciting opportunities in AI/ML, research positions, and consulting projects
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
